import { type GetDashboardDataResponse } from '@consigli/api-client';
import { ConflictCategory, ConflictType } from '@consigli/types';

export type ConflictTypesCount = GetDashboardDataResponse['conflictTypesCount'];

/** One dashboard card row: API bucket + `{ CONFLICT_TYPE: count }`. */
export type DashboardConflictCard = {
  navConflictCategory: ConflictCategory;
  conflictType: Record<string, number>;
};

/**
 * Either the DPR "Error checker" block (Insight + Quality counts merged),
 * or a normal heading from `dashboard.conflict-category.*`.
 */
export type DashboardConflictSection =
  | { kind: 'dpr-error-checker'; cards: DashboardConflictCard[] }
  | { kind: 'by-category'; category: keyof ConflictTypesCount; cards: DashboardConflictCard[] };

const DPR_MERGED_CATEGORIES = new Set<string>([
  ConflictCategory.INSIGHT,
  ConflictCategory.MISSING_DOCUMENTATION,
]);

const RFP_EXCLUDED_CONFLICT_TYPES = new Set<string>([
  ConflictType.MILESTONE,
  ConflictType.DELIVERABLE,
]);

export const conflictCategoryToSortIndex = (conflictCategory: keyof ConflictTypesCount): number => {
  switch (conflictCategory) {
    case ConflictCategory.MISSING_DOCUMENTATION:
      return 1;
    case ConflictCategory.RISK:
      return 2;
    case ConflictCategory.INSIGHT:
      return 3;
    case ConflictCategory.RENTAL_AGREEMENT:
      return 4;
    case ConflictCategory.COMPANY:
      return 5;
    case ConflictCategory.PROPERTY:
      return 6;
    case ConflictCategory.GAPS:
      return 7;
    default:
      return 99;
  }
};

function sortedCategoryKeys(raw: ConflictTypesCount): string[] {
  return Object.keys(raw).sort(
    (a, b) =>
      conflictCategoryToSortIndex(a as keyof ConflictTypesCount) -
      conflictCategoryToSortIndex(b as keyof ConflictTypesCount),
  );
}

function cardsForCategory(
  raw: ConflictTypesCount,
  category: string,
  excludedConflictTypes?: Set<string>,
): DashboardConflictCard[] {
  const rows = raw[category as keyof ConflictTypesCount] ?? [];
  const navConflictCategory = category as ConflictCategory;
  return rows
    .filter((conflictType) => {
      if (!excludedConflictTypes) return true;
      const typeId = Object.keys(conflictType)[0];
      return !excludedConflictTypes.has(typeId);
    })
    .map((conflictType) => ({ navConflictCategory, conflictType }));
}

function buildDprErrorCheckerCards(raw: ConflictTypesCount): DashboardConflictCard[] {
  return [
    ...cardsForCategory(raw, ConflictCategory.INSIGHT),
    ...cardsForCategory(raw, ConflictCategory.MISSING_DOCUMENTATION),
  ];
}

function sectionsDefault(
  raw: ConflictTypesCount,
  excludedConflictTypes?: Set<string>,
): DashboardConflictSection[] {
  return sortedCategoryKeys(raw).map((category) => ({
    kind: 'by-category' as const,
    category: category as keyof ConflictTypesCount,
    cards: cardsForCategory(raw, category, excludedConflictTypes),
  }));
}

function sectionsDesignPackageReview(raw: ConflictTypesCount): DashboardConflictSection[] {
  const sections: DashboardConflictSection[] = [];

  for (const category of sortedCategoryKeys(raw)) {
    if (DPR_MERGED_CATEGORIES.has(category)) continue;
    sections.push({
      kind: 'by-category',
      category: category as keyof ConflictTypesCount,
      cards: cardsForCategory(raw, category),
    });
  }

  const errorCheckerCards = buildDprErrorCheckerCards(raw);
  if (errorCheckerCards.length > 0) {
    sections.push({ kind: 'dpr-error-checker', cards: errorCheckerCards });
  }

  return sections;
}

/**
 * Turns API `conflictTypesCount` into dashboard sections (headings + card rows).
 * For Design Package Review, Insight and Missing documentation (Quality) share one "Error checker" block.
 * For RFP packages, MILESTONE and DELIVERABLE types are excluded since they are shown in dedicated components.
 */
export function buildDashboardConflictSections(
  conflictTypesCount: ConflictTypesCount | undefined,
  isDesignPackageReview: boolean,
  isRfpPackage?: boolean,
): DashboardConflictSection[] {
  if (conflictTypesCount == null) return [];
  const excludedConflictTypes = isRfpPackage ? RFP_EXCLUDED_CONFLICT_TYPES : undefined;
  return isDesignPackageReview
    ? sectionsDesignPackageReview(conflictTypesCount)
    : sectionsDefault(conflictTypesCount, excludedConflictTypes);
}

export function conflictSectionTitleKey(
  section: DashboardConflictSection,
  isWaterService?: boolean,
): string {
  if (section.kind === 'dpr-error-checker') {
    return 'dashboard.error-checker';
  }
  if (isWaterService && section.category === ConflictCategory.RISK) {
    return 'dashboard.conflict-category.RISK_WATER';
  }
  return `dashboard.conflict-category.${String(section.category)}`;
}

export function conflictSectionReactKey(section: DashboardConflictSection): string {
  return section.kind === 'dpr-error-checker' ? 'dpr-error-checker' : String(section.category);
}
